import { useMemo, useState } from "react";
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
} from "recharts";
import { FIELD_NAME_TO_TRANSLATION_LABEL, LineChartProps } from "./LineChartCommon";
import { useTranslation } from "react-i18next";
import { downRounding, upRounding } from "./LineChartCommon";
import { isTriPhaseDevice } from "../../../../lib/Device";

import _ from "lodash"

interface PowerLineChartProps extends LineChartProps {
    focusMode: boolean
}

const PHASE_POWER_KEYS = ["p_1_soliseco", "p_2_soliseco", "p_3_soliseco"] as const;

const PHASE_POWER_COLORS = {
    p_1_soliseco: "#5D6D7E",
    p_2_soliseco: "#3b6bb8",
    p_3_soliseco: "#9B59B6",
} as const;

const PowerLineChart = ({ lineData, currentDevice, ticks, lineChartXTickFormatter, focusMode }: PowerLineChartProps) => {

    const { t } = useTranslation("dashboard");
    const isTriPhase = isTriPhaseDevice(currentDevice);
    // Phase powers hidden by default; user can enable them from the legend.
    const [hiddenSeries, setHiddenSeries] = useState<Record<string, boolean>>({
        p_1_soliseco: true,
        p_2_soliseco: true,
        p_3_soliseco: true,
    });

    const translateSeries = (key: string) =>
        t(FIELD_NAME_TO_TRANSLATION_LABEL[key] ?? key, {
            device_name: currentDevice?.name,
        });

    const isSeriesHidden = (key: string) => !!hiddenSeries[key];

    const toggleSeries = (key: string) => {
        setHiddenSeries((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    const renderCustomizedTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            const pt = lineData.find((elem: any) => elem.time === label);
            if (pt) {
                return (
                <div className="tooltip-container">
                    <p style={{ fontWeight: 700 }}>{lineChartXTickFormatter(parseInt(pt.time))}</p>
                    {payload.map((line: any, index: number) => (
                    <p key={index} style={{ color: line.stroke, fontWeight: 500 }}>
                        {translateSeries(line.name ?? line.dataKey)}:{" "}
                        {pt[line.dataKey]} W
                    </p>
                    ))}
                </div>
                );
            }
        }
        return <></>;
    };

    const filteredLineData = useMemo(() => {
        if (focusMode) {
            const firstIdx = _.findIndex(lineData, (elem: any) => elem.soliseco_power > 20)
            const lastIdx = _.findLastIndex(lineData, (elem: any) => elem.soliseco_power > 20)
            if (firstIdx + 10 < lastIdx) {
                return lineData.slice(Math.max(0, firstIdx - 5), Math.min(lineData.length - 1, lastIdx + 5))
            }
        }
        return lineData
    }, [focusMode, lineData])

    const [dataMin, dataMax] = useMemo(() => {
        const keys = ["power_balance", "power_balance_no_soliseco", "soliseco_power"];
        if (isTriPhase) {
            for (const key of PHASE_POWER_KEYS) {
                if (!hiddenSeries[key]) {
                    keys.push(key);
                }
            }
        }
        const values = filteredLineData.flatMap((pt: any) =>
            keys.map((key) => Number(pt[key] ?? 0))
        );
        const globalMin = Math.min(...values)
        const globalMax = Math.max(...values)
        const min: number = Math.round((globalMin * 1.1))
        const roundedMin = min - (10 + (min % 10))
        const max: number = Math.round((globalMax * 1.1))
        const roundedMax =  max + (10 - (max % 10))
        return [Math.min(-100, parseInt(roundedMin.toFixed(0))), Math.max(100, parseInt(roundedMax.toFixed(0)))]
    }, [filteredLineData, isTriPhase, hiddenSeries])

    const filteredTicks = useMemo(() => {
        if (filteredLineData) {
            if (filteredLineData.length > 1) {
                const start = filteredLineData[0].time
                const end = filteredLineData[filteredLineData.length - 1].time
                const roundedStart = upRounding(start, 900)
                const roundedEnd = downRounding(end, 900)
                const diff = roundedEnd - roundedStart
                if (diff > 3600) {
                const secondTick = downRounding(roundedStart + (diff / 3), 900)
                const thirdTick = downRounding(roundedStart + ((2 * diff) / 3), 900)
                return [roundedStart, secondTick, thirdTick, roundedEnd]

                }
                if (roundedEnd - roundedStart >= 900) {
                return [roundedStart, roundedEnd]
                }
            }
            return filteredLineData.map((elem: any) => elem.time)
        }
        return []
    }, [filteredLineData])

    const compareNumbers = (a: number, b: number) => {
        return a - b;
    }
    
    const yTicks = useMemo(() => {
        const interval = Math.round((Math.abs(dataMin) + Math.abs(dataMax)) / 4)
        const roundedInterval = interval + (10 - (interval % 10))
        const negativeTickCnt: number = Math.floor(Math.abs(dataMin) / roundedInterval)
        const positiveTickCnt: number = Math.floor(Math.abs(dataMax) / roundedInterval)
        const negativeTicks = Array.from({ length: negativeTickCnt }, (_, i) => { return -(i + 1) * roundedInterval })
        const positiveTicks = Array.from({ length: positiveTickCnt }, (_, i) => { return (i + 1) * roundedInterval })
        const ticks = [dataMin, ...negativeTicks, 0, ...positiveTicks, dataMax]
        const uniqueTicks = ticks.filter((value, index, array) => ticks.indexOf(value) === index);
        uniqueTicks.sort(compareNumbers)
        return uniqueTicks
    }, [dataMin, dataMax])

    return (
        <ResponsiveContainer width="100%" height="100%">
            <LineChart
                style={{ marginLeft: "-5px" }}
                width={500}
                height={300}
                data={filteredLineData}
            >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                    dataKey='time'
                    name='Time'
                    scale='time'
                    type='number'
                    domain={['dataMin', 'dataMax']} 
                    tickFormatter={ lineChartXTickFormatter }
                    ticks={filteredTicks}
                />
                <YAxis
                    unit={"W"}
                    domain={ () => [dataMin, dataMax] }
                    ticks={yTicks}
                />
                <Tooltip content={renderCustomizedTooltip} />
                <Legend
                    formatter={(value) => (
                        <span
                            style={{
                                opacity: isSeriesHidden(value) ? 0.35 : 1,
                                cursor: "pointer",
                            }}
                        >
                            {translateSeries(value)}
                        </span>
                    )}
                    onClick={(entry) => {
                        const key = String(entry.value ?? entry.dataKey ?? "");
                        if (key) toggleSeries(key);
                    }}
                />
                <Line
                    type="monotone"
                    dataKey={"power_balance_no_soliseco"}
                    name="power_balance_no_soliseco"
                    hide={isSeriesHidden("power_balance_no_soliseco")}
                    stroke="#012124"
                    activeDot={{ r: 4 }}
                    dot={false}
                />
                <Line
                    type="monotone"
                    dataKey={"power_balance"}
                    name="power_balance"
                    hide={isSeriesHidden("power_balance")}
                    stroke="#F7AA3F"
                    activeDot={{ r: 4 }}
                    dot={false}
                />
                <Line
                    type="monotone"
                    dataKey={"soliseco_power"}
                    name="soliseco_power"
                    hide={isSeriesHidden("soliseco_power")}
                    stroke="#82BA71"
                    activeDot={{ r: 4 }}
                    dot={false}
                />
                {isTriPhase && (
                    <>
                        <Line
                            type="monotone"
                            dataKey="p_1_soliseco"
                            name="p_1_soliseco"
                            hide={isSeriesHidden("p_1_soliseco")}
                            stroke={PHASE_POWER_COLORS.p_1_soliseco}
                            strokeDasharray="4 3"
                            activeDot={{ r: 4 }}
                            dot={false}
                        />
                        <Line
                            type="monotone"
                            dataKey="p_2_soliseco"
                            name="p_2_soliseco"
                            hide={isSeriesHidden("p_2_soliseco")}
                            stroke={PHASE_POWER_COLORS.p_2_soliseco}
                            strokeDasharray="4 3"
                            activeDot={{ r: 4 }}
                            dot={false}
                        />
                        <Line
                            type="monotone"
                            dataKey="p_3_soliseco"
                            name="p_3_soliseco"
                            hide={isSeriesHidden("p_3_soliseco")}
                            stroke={PHASE_POWER_COLORS.p_3_soliseco}
                            strokeDasharray="4 3"
                            activeDot={{ r: 4 }}
                            dot={false}
                        />
                    </>
                )}
            </LineChart>
        </ResponsiveContainer>
    )
}

export default PowerLineChart
